import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, Grid, LinearProgress, styled, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Chip, { ChipStatus } from 'src/frontend-common/components/Chip';
import Column from 'src/frontend-common/components/Column';
import Ellipsis from 'src/frontend-common/components/Ellipsis';
import EmptyState from 'src/frontend-common/components/EmptyState';
import FailureScreen from 'src/frontend-common/components/FailureScreen';
import LoadingIcon from 'src/frontend-common/components/Icons/Loading';
import { Rule, useGetRuleRaccomendationQuery } from 'src/redux/services/imports';
import { capitalizedLowercase } from 'src/utils/string';

interface RuleRaccomedations {
  appKey: string;
}
const LoadingProgressWrapper = styled('div')<{ showProgress: boolean }>(({ showProgress }) => ({
  height: 50,
  opacity: showProgress ? 1 : 0,
  transition: 'opacity 0.5s',
}));

const RuleRaccomendations: React.FC<RuleRaccomedations> = ({ appKey }) => {
  const id = useParams<{ id: string }>();
  const [progress, setProgress] = useState<number>(-1);
  const [status, setStatus] = useState<string>('IN_PROGRESS');
  const [isEmptyObject, setIsEmptyObject] = useState<boolean>(false);
  const [retry, setRetry] = useState<number>(0);
  const theme = useTheme();
  let interval: NodeJS.Timeout;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { data, isLoading, error } = useGetRuleRaccomendationQuery(
    {
      app_key: appKey,
      key: id.id || '',
    },
    {
      pollingInterval: 3000,
      skip: status === 'COMPLETED' || isEmptyObject || retry === 10,
    }
  );

  useEffect(() => {
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (error) interval = setInterval(() => setRetry((prevCount) => prevCount + 1), 3000);
    if (retry === 10) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [error, retry]);

  useEffect(() => {
    if (data && data.partial_count !== 0) setProgress(Math.floor((data?.partial_count / data.total_count) * 100));
    else if (data?.partial_count === 0) {
      setProgress(0);
    }
  }, [data?.partial_count]);

  useEffect(() => {
    if (data) setIsEmptyObject(Object.keys(data).length === 0 && data.constructor === Object);
  }, [data]);

  useEffect(() => {
    if (data) {
      setStatus(data.status);
      if (data.status === 'COMPLETED') {
        clearInterval(interval);
        setRetry(0);
      }
    }
  }, [data?.status]);

  const renderRule = (rule: Rule) => {
    return (
      <Grid container>
        {['Product', 'Signal', 'Comparator', 'Value'].map((label) => {
          return (
            <Grid key={label} item md={label === 'Comparator' ? 2.5 : label === 'Value' ? 1.9 : 3.55} xs={6} sx={{ marginTop: isMobile ? '10px' : 0 }}>
              <Column>
                <Typography variant="h5" style={{ fontSize: 14 }}>
                  {label}
                </Typography>
                <>
                  {rule.conditions.map((condition) => {
                    switch (label) {
                      case 'Product':
                        return (
                          <Typography variant="body2" style={{ marginTop: 8 }}>
                            {capitalizedLowercase(condition.product)}
                          </Typography>
                        );
                      case 'Signal':
                        return (
                          <Typography variant="body2" style={{ marginTop: 8 }}>
                            {condition.target}
                          </Typography>
                        );
                      case 'Comparator':
                        return (
                          <Typography variant="body2" style={{ marginTop: 8 }}>
                            {' '}
                            {condition.comparator}
                          </Typography>
                        );
                      case 'Value':
                        return (
                          <Typography variant="body2" style={{ marginTop: 8 }}>
                            {condition.value.toString()}
                          </Typography>
                        );
                    }
                  })}
                </>
              </Column>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const renderGeneralInfo = (accordion_title: string, action_title: string, title: string, effect: JSX.Element | string) => {
    return (
      <Grid container style={{ marginBottom: '16px' }}>
        <Grid item md={7.08} xs={12}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2">{title} </Typography>

            <Ellipsis
              variant="h5"
              style={{
                fontSize: 14,
                width: isMobile ? '200px' : '88%',
                marginLeft: '5px',
              }}
            >
              {accordion_title || ''}
            </Ellipsis>
          </div>
        </Grid>
        <Grid item md={2.45} xs={12}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2">Action: </Typography>
            <Typography variant="h5" style={{ fontSize: 14, marginLeft: '5px' }}>
              {action_title}
            </Typography>
            <div style={{ marginLeft: '5px' }}> {effect}</div>
          </div>
        </Grid>
      </Grid>
    );
  };

  if (data && Object.keys(data).length === 0 && data.constructor === Object) {
    return (
      <Box
        style={{
          position: 'relative',
          backgroundColor: 'white',
          width: '100%',

          marginLeft: 24,
        }}
      >
        <EmptyState style={{ width: '100%', padding: '80px 20px' }} />
      </Box>
    );
  } else if (retry === 2) {
    return <FailureScreen>An error occurred, please retry</FailureScreen>;
  } else if (status === 'IN_PROGRESS') {
    return (
      <div
        style={{
          width: '100%',
          height: '400px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'white',
          marginLeft: '24px',
        }}
      >
        {isLoading && (
          <div style={{ height: 60 }}>
            <LoadingIcon />
          </div>
        )}
        <LoadingProgressWrapper showProgress={progress >= 0 && progress <= 100}>
          {progress >= 0 && progress <= 100 && (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '270px',
                  maxWidth: '270px',
                }}
              >
                <LinearProgress variant="determinate" value={progress} style={{ minWidth: 250, maxWidth: 250 }} />
                <Typography variant="body2">{`${progress}%`}</Typography>
              </div>
              <div style={{ marginTop: 8 }}>
                <Typography variant="body2">Generating recommendations for your model...</Typography>
              </div>
            </>
          )}
        </LoadingProgressWrapper>
      </div>
    );
  } else if (status === 'COMPLETED' && data?.model_change_suggestions.length === 0) {
    return (
      <Box
        style={{
          position: 'relative',
          backgroundColor: 'white',
          width: '100%',

          marginLeft: 24,
        }}
      >
        <EmptyState style={{ width: '100%', padding: '80px 20px' }} />
      </Box>
    );
  } else {
    const model_change_suggestions_copy = [...(data?.model_change_suggestions || [])];
    return (
      <div style={{ width: '100%', paddingLeft: 24 }}>
        <div
          style={{
            width: '100%',
            height: 73,
            backgroundColor: 'white',
            display: 'flex',
            padding: 24,
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h5">{data?.model_name}</Typography>
          <Typography variant="h5">Number of recommendations: {data?.model_change_suggestions.length}</Typography>
        </div>

        {model_change_suggestions_copy
          .sort((a, b) => b.performance_change - a.performance_change)
          .map((suggestion, i) => {
            const accordion_title = suggestion.old_rule && !suggestion.new_rule ? suggestion.old_rule.name : suggestion.new_rule?.name;

            let action_title = suggestion.old_rule && !suggestion.new_rule ? suggestion.old_rule.type : suggestion.new_rule?.type;
            if (action_title === 'score_gate') action_title = 'Limit to cluster';
            else action_title = 'Score';

            const category =
              suggestion.new_rule && !suggestion.old_rule
                ? 'Add rule'
                : suggestion.new_rule && suggestion.old_rule
                  ? 'Edit rule'
                  : suggestion.old_rule && !suggestion.new_rule
                    ? 'Remove rule'
                    : '';

            const effect = (title: string) => {
              const values = [];
              if (category === 'Add rule' || category === 'Edit rule') {
                if (title === 'Limit to cluster') {
                  values.push(
                    <Typography variant="h5" style={{ fontSize: 14 }}>
                      {suggestion.new_rule?.effect.destination}
                    </Typography>
                  );
                }
                if (title === 'Score') {
                  const up_or_down = suggestion.new_rule?.effect.direction === 'up' ? '+' : '-';
                  values.push(
                    <Typography variant="h5" style={{ fontSize: 14, marginLeft: '2px' }}>{`${up_or_down}${suggestion.new_rule?.effect.weight}`}</Typography>
                  );
                }
              }
              if (category === 'Remove rule' || category === 'Edit rule') {
                if (title === 'Limit to cluster') {
                  values.push(
                    <Typography variant="h5" style={{ fontSize: 14 }}>
                      {suggestion.old_rule?.effect.destination}
                    </Typography>
                  );
                }
                if (title === 'Score') {
                  const up_or_down = suggestion.old_rule?.effect.direction === 'up' ? '+' : '-';
                  values.push(
                    <Typography variant="h5" style={{ fontSize: 14, marginLeft: '2px' }}>{`${up_or_down}${suggestion.old_rule?.effect.weight}`}</Typography>
                  );
                }
              }

              return values;
            };

            const getPerformanceLevel = (value: number): { text: string; status: ChipStatus } => {
              if (value > 10) return { text: 'HIGH', status: 'unknown' };
              if (value > 5) return { text: 'MEDIUM', status: 'unknown' };
              return { text: 'LOW', status: 'unknown' };
            };

            const performanceInfo = getPerformanceLevel(suggestion.performance_change);

            return (
              <Accordion key={i}>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    width: '100%',
                    flexDirection: 'row-reverse',
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                      transform: 'rotate(-90deg)',

                      marginLeft: '-5px',
                      marginTop: '-16px',
                    },
                    '& .MuiSvgIcon-root': {
                      marginRight: '5px',
                      marginLeft: '-7px',
                    },
                  }}
                >
                  <Grid container>
                    <Grid item md={7} xs={12}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Typography variant="body2">Rule: </Typography>

                        <Ellipsis
                          variant="h5"
                          style={{
                            fontSize: 14,
                            width: isMobile ? '200px' : '90%',

                            marginLeft: '5px',
                          }}
                        >
                          {accordion_title || ''}
                        </Ellipsis>
                      </div>
                    </Grid>
                    <Grid item md={2.5} xs={12}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2">Action: </Typography>
                        <Typography variant="h5" style={{ fontSize: 14, marginLeft: '5px' }}>
                          {action_title}
                        </Typography>
                        <div style={{ marginLeft: '5px' }}> {effect(action_title)[0]}</div>
                      </div>
                    </Grid>
                    <Grid item md={2} xs={12}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2">Category: </Typography>
                        <Typography variant="h5" style={{ fontSize: 14, marginLeft: '5px' }}>
                          {category}
                        </Typography>
                      </div>
                    </Grid>
                    <Grid item md={0.5} xs={12}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Chip status={performanceInfo.status}>{performanceInfo.text}</Chip>
                      </div>
                    </Grid>
                  </Grid>
                </AccordionSummary>
                <AccordionDetails>
                  {category === 'Add rule' && suggestion.new_rule && renderRule(suggestion.new_rule)}

                  {category === 'Edit rule' && suggestion.new_rule && suggestion.old_rule && (
                    <>
                      {renderGeneralInfo(
                        suggestion.new_rule.name,
                        suggestion.new_rule.type === 'score_gate' ? 'Limit to cluster' : 'Score',
                        'New rule:',
                        effect(suggestion.new_rule.type === 'score_gate' ? 'Limit to cluster' : 'Score')[0] || ''
                      )}
                      {renderRule(suggestion.new_rule)}
                      <Divider sx={{ marginTop: '24px', marginBottom: '24px' }} />
                      {renderGeneralInfo(
                        suggestion.old_rule.name,
                        suggestion.old_rule.type === 'score_gate' ? 'Limit to cluster' : 'Score',
                        'Old rule:',
                        effect(suggestion.old_rule.type === 'score_gate' ? 'Limit to cluster' : 'Score')[1] || ''
                      )}
                      {renderRule(suggestion.old_rule)}
                    </>
                  )}

                  {category === 'Remove rule' && suggestion.old_rule && renderRule(suggestion.old_rule)}
                </AccordionDetails>
              </Accordion>
            );
          })}
      </div>
    );
  }
};

export default RuleRaccomendations;
